import React, { useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { DatabaseContext } from '../../redux/DatabaseStore';
import { deleteRequest, getRequest, postRequest, tabulatorGet, downloadBlobGet, fetchTextGet } from '../../api/ApiController';
import { InfoIcon, ErrorIcon, TaskIcon, PermissionIcon, NeedIcon, EmergencyIcon, EditRowIcon, DeleteIcon, UnseenIcon, SeenIcon, RefreshIcon, PrintIcon, ExportIcon, VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon } from "../svg/svgIcons";
import AuthContext from '../../components/AuthContext';
import UtilContext from '../../components/UtilContext';
import { LocaleContext } from '../../components/LocaleContext';
import { confirmDialog } from 'primereact/confirmdialog';
import '../../assets/css/support.css';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import { Avatar } from 'primereact/avatar';
import { DateTime } from "luxon";
import { Tag } from 'primereact/tag';
import { PanelMenu } from 'primereact/panelmenu';
import { extractSafePaginationParams, createDedupedAjaxRequest, parseSorters, refreshProgressiveFromStart, mergeSupportTicketPagedListFiltersAndSorters, safeParse, sanitizeTabulatorLayoutAttributes } from '../../utils/tabulatorProgressiveLoading';
import {
    SUPPORT_TICKET_SCOPE_OPEN,
    applySupportTicketListScopeStatusParam,
    buildSupportTicketStatusFilterOptions,
    formatSupportTicketStatusLabel,
    headerFilterMatchesSupportTicketStatus,
    normalizeSupportTicketListScope,
    ticketMatchesSupportListFilter,
} from '../../utils/supportTicketStatus';
import { updateSupportTicketTabulatorRowInPlace } from '../../utils/supportTicketListRowPatch';
import { getTimezone } from '../../utils/moduleSettings';
import { getPusherChannel } from '../../utils/pusherChannel';
import { addLocale } from 'primereact/api';
import { primeMultiSelectHeaderFilter } from '../../utils/primeMultiSelectHeaderFilter';
import { dateUserHeaderFilter, DATE_USER_HEADER_FILTER_COL_MIN_WIDTH } from '../../utils/dateUserHeaderFilter';
import { inputOnEnterHeaderFilter } from '../../utils/inputOnEnterHeaderFilter';
import { scrollToFirst as scrollToFirstUtil, scrollUp as scrollUpUtil, scrollDown as scrollDownUtil, scrollToLast as scrollToLastUtil } from '../../utils/tabulatorScroll';
import { getTabulatorLayoutForStandardMask } from '../../utils/tabulatorLayoutUtils';
import PrintOptionsDialog from '../../components/PrintOptionsDialog';
import { buildSupportTicketExportQueryStringFromParams, getSupportTicketPrintColumnOrder } from '../../utils/supportTicketExport';
import { openPrintHtmlWindow, triggerBlobDownload } from '../../utils/serviceTaskExport';
import { getEffectiveMask } from '../../utils/effectiveMask';
import { EXPORT_DOCUMENT_TITLE_KEYS, excelSheetTitleFromDocumentTitle, xlsxDownloadFilename } from '../../utils/exportExcelNaming';
import TabulatorMaskDialog from '../../utils/tabulatorMaskDialog';
import { defaultProductLogoSrc } from '../../constants/brandingDisplay';
import { buildCanonicalPrintFooterHtml, buildCanonicalPrintHeaderHtml } from '../../utils/printChrome';
import { uiT } from '../../utils/uiTranslate';
import { applyTabulatorRuntimeI18n } from '../../utils/tabulatorRuntimeI18n';

const SUPPORT_TICKET_COLUMN_I18N_KEYS = Object.freeze({
  id: 'SupportTicketTable.Col.Id',
  type: 'SupportTicketTable.Col.Type',
  defined_id: 'SupportTicketTable.Col.DefinedId',
  priority: 'SupportTicketTable.Col.Priority',
  theme: 'SupportTicketTable.Col.Theme',
  status: 'SupportTicketTable.Col.Status',
  mask_number: 'SupportTicketTable.Col.MaskNumber',
  waiting_for_action_by: 'SupportTicketTable.Col.WaitingDeadline',
  created_at: 'SupportTicketTable.Col.Created',
  updated_at: 'SupportTicketTable.Col.Updated',
  custom_delete: 'SupportTicketTable.Col.Delete',
});

addLocale('sk', {
    firstDayOfWeek: 1,
    dayNames: ['Nedeľa','Pondelok','Utorok','Streda','Štvrtok','Piatok','Sobota'],
    dayNamesShort: ['Ne','Po','Ut','St','Št','Pi','So'],
    dayNamesMin: ['Ne','Po','Ut','St','Št','Pi','So'],
    monthNames: ['Január','Február','Marec','Apríl','Máj','Jún','Júl','August','September','Október','November','December'],
    monthNamesShort: ['Jan','Feb','Mar','Apr','Máj','Jún','Júl','Aug','Sep','Okt','Nov','Dec'],
    today: uiT('Common.Today'),
    clear: uiT('Common.Delete'),
    chooseDate: uiT('Common.ChooseDate')
});

export default function SupportTicketList() {

    const [state, dispatch] = useContext(DatabaseContext);
    const { user } = useContext(AuthContext);
    const { getUserColor, getUserInitials, setUserMaskFilter, setStandardMaskFilter, toast, setDialogText, setVisible, getDateFormat, formatDate, openFavoriteDialog, setFastView } = useContext(UtilContext);
    const { t, locale } = useContext(LocaleContext);
    const tRef = useRef(t);
    tRef.current = t;
    const timezone = getTimezone(state?.module_settings || []);

    const ref = useRef(null);
    const inFlightRef = useRef({ sig: null, promise: null });
    const completedCacheRef = useRef(new Map());
    const tableBuiltRef = useRef(false);
    const lastResponseRef = useRef(null);
    const savedHScrollRef = useRef({ left: 0, pending: false });
    const isRestoringRef = useRef(false);
    const userInteractedRef = useRef(false);
    const maskAppliedRef = useRef(false);
    const overlayRef = useRef(null);
    const tableContainerRef = useRef(null);
    const resetToFirstPageRef = useRef(false);
    const pendingRowIdsRef = useRef(new Set());
    const needsRedrawRef = useRef(false);
    const rafRedrawRef = useRef(null);
    const currentFilterRef = useRef({ statusValue: null, contractorValue: null, typeValue: null, user: null });
    const pendingTicketUpdateRef = useRef(null);

    const [statusValue, setStatusValue] = useState(SUPPORT_TICKET_SCOPE_OPEN);
    const [contractorValue, setContractorValue] = useState("Všetky");
    const [typeValue, setTypeValue] = useState("All");
    const DEFAULT_COLUMNS_VISIBLE_SUPPORT = { id: true, type: true, is_unread: true, defined_id: true, priority: true, theme: true, status: true, mask_number: true, waiting_for_action_by: true, created_at: true, updated_at: true, custom_delete: true };
    const [columnsVisible, setColumnsVisible] = useState({ ...DEFAULT_COLUMNS_VISIBLE_SUPPORT });
    const [maskVisible, setMaskVisible] = useState(false);
    const [printChoiceVisible, setPrintChoiceVisible] = useState(false);
    const [printOrientationChoice, setPrintOrientationChoice] = useState('landscape');
    const [printLayoutChoice, setPrintLayoutChoice] = useState('branded');
    const [expandedKeys, setExpandedKeys] = useState({});
    // ReactTabulator binds events once on mount. Keep top-filter values in a ref so
    // dataChanged / ajax params never close over the initial Otvorené/Všetky/All.
    currentFilterRef.current = { statusValue, contractorValue, typeValue, user };

    const getTable = useCallback(() => ref.current?.table ?? ref.current, []);
    const applyDefaultListLayout = useCallback(() => {
        const table = getTable();
        if (!table) return;
        try {
            table.clearHeaderFilter?.();
            table.setSort?.([]);
            const defaults = { ...DEFAULT_COLUMNS_VISIBLE_SUPPORT };
            setColumnsVisible(defaults);
            Object.keys(defaults).forEach((field) => {
                if (defaults[field] === false) table.hideColumn?.(field);
                else table.showColumn?.(field);
            });
            table.redraw?.(true);
        } catch (_) {}
    }, [getTable]);

    const isTableVisible = useCallback(() => {
        const table = getTable();
        if (!table) return false;
        const el = table.getElement?.() ?? table.element ?? table.rowManager?.element?.parentElement;
        return !!(el && el.offsetParent != null && el.clientHeight > 0);
    }, [getTable]);

    const ticketMatchesFilter = useCallback((data, f) => {
        const headerStatusValues = getTable()?.getHeaderFilters?.()?.find((hf) => hf?.field === 'status')?.value;
        return ticketMatchesSupportListFilter(data, { ...f, headerStatusValues });
    }, [getTable]);

    const scheduleRedraw = useCallback(() => {
        if (rafRedrawRef.current != null) return;
        rafRedrawRef.current = requestAnimationFrame(() => {
            const table = getTable();
            rafRedrawRef.current = null;
            if (!table) return;
            const ids = Array.from(pendingRowIdsRef.current);
            pendingRowIdsRef.current.clear();
            needsRedrawRef.current = false;
            // Phase 5A: keep loaded rows visible after mutation even if they no longer
            // match the active filter/sort. Full reconcile = Refresh / filter / sort / restart.
            ids.forEach((id) => {
                const row = table.getRow?.(id);
                if (!row) return;
                const data = row.getData?.();
                if (!data) return;
                row.reformat?.();
            });
        });
    }, [getTable]);

    const captureHScroll = () => {
        const table = getTable();
        if (!table) return;
        const holder = table.rowManager?.element;
        if (!holder) return;
        savedHScrollRef.current = { left: holder.scrollLeft, pending: true };
    };

    const restoreHScroll = () => {
        if (!savedHScrollRef.current.pending) return;
        const table = getTable();
        if (!table) return;
        const holder = table.rowManager?.element;
        const header = table.columnManager?.contentsElement;
        if (!holder) return;
        const left = savedHScrollRef.current.left;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                holder.scrollLeft = left;
                if (header) header.scrollLeft = left;
                savedHScrollRef.current.pending = false;
            });
        });
    };
    const refreshFromStart = useCallback(() => refreshProgressiveFromStart(getTable, completedCacheRef, t('SupportTicketList.TableLoadingData')), [getTable, t]);

    const pageSizePc = useMemo(() => {
        const v = (state.module_settings || []).find(s => s.module === 'global' && s.key === 'page_size_pc')?.value;
        const n = parseInt(v, 10);
        return (Number.isFinite(n) && n > 0) ? n : 50;
    }, [state.module_settings]);

    const initialMaskParams = useMemo(() => {
        const effective = getEffectiveMask('SupportTicketList', state);
        if (!effective || effective.type !== 'user' || !effective.userMask) return {};
        const list = [effective.userMask];
        const rawFilters = safeParse(list[0].filters, {});
        const rf = rawFilters && typeof rawFilters === 'object' && !Array.isArray(rawFilters) ? rawFilters : {};
        const out = {};
        if (rf.contractorValue && rf.contractorValue !== 'Všetky') out.filter_contractorValue = rf.contractorValue;
        if (rf.typeValue && rf.typeValue !== 'All') out.filter_type = rf.typeValue;
        if (rf.theme != null && String(rf.theme).trim()) out.filter_theme = String(rf.theme).trim();
        if (rf.defined_id != null && String(rf.defined_id).trim()) out.filter_defined_id = String(rf.defined_id).trim();
        if (rf.priority != null && String(rf.priority).trim()) out.filter_priority = String(rf.priority).trim();
        if (rf.status != null && String(rf.status).trim()) out.filter_status = String(rf.status).trim();
        if (rf.mask_number != null && String(rf.mask_number).trim()) out.filter_mask_number = String(rf.mask_number).trim();
        if (rf.waiting_for_action_by != null && String(rf.waiting_for_action_by).trim()) out.filter_waiting_for_action_by = String(rf.waiting_for_action_by).trim();
        if (rf.created_at != null && String(rf.created_at).trim()) out.filter_created_at = String(rf.created_at).trim();
        if (rf.updated_at != null && String(rf.updated_at).trim()) out.filter_updated_at = String(rf.updated_at).trim();
        if (rf.is_unread === true || rf.is_unread === 'true') out.filter_is_unread = '1';
        else if (rf.is_unread === false || rf.is_unread === 'false') out.filter_is_unread = '0';
        applySupportTicketListScopeStatusParam(out, rf.statusValue);
        const sorters = parseSorters(list[0].sorters);
        if (sorters.length) out.sorters = JSON.stringify(sorters.map((s) => ({ field: s.field || s.column, dir: s.dir })));
        return out;
    }, [state.user_mask_filters, state.standard_mask_filters]);

    const buildSupportTicketQueryParams = useCallback((paramsFromTabulator = {}, opts = {}) => {
        const { includePagination = true, consumeResetPage = true } = opts;
        const extra = includePagination ? { ...extractSafePaginationParams(paramsFromTabulator) } : {};
        if (consumeResetPage && resetToFirstPageRef.current) {
            if (includePagination) extra.page = 1;
            resetToFirstPageRef.current = false;
        }
        if (includePagination && extra.size == null) extra.size = pageSizePc;

        const top = currentFilterRef.current;
        const status = top.statusValue ?? statusValue;
        const contractor = top.contractorValue ?? contractorValue;
        const type = top.typeValue ?? typeValue;

        if (contractor && contractor !== 'Všetky') extra.filter_contractorValue = contractor;
        if (type && type !== 'All') extra.filter_type = type;

        const tableReadyForParams = tableBuiltRef.current;
        mergeSupportTicketPagedListFiltersAndSorters(extra, {
            getTable,
            tableBuiltForParams: tableReadyForParams,
            userMaskFilters: state.user_mask_filters,
            maskPage: 'SupportTicketList',
            module_settings: state?.module_settings,
            getDateFormat,
        });

        if (!tableReadyForParams) {
            Object.keys(initialMaskParams).forEach((k) => {
                if (extra[k] === undefined) extra[k] = initialMaskParams[k];
            });
        }

        if (contractor && contractor !== 'Všetky') extra.filter_contractorValue = contractor;
        if (type && type !== 'All') extra.filter_type = type;
        applySupportTicketListScopeStatusParam(extra, status);

        return extra;
    }, [getTable, state.user_mask_filters, state?.module_settings, statusValue, contractorValue, typeValue, initialMaskParams, pageSizePc, getDateFormat]);

    const buildRequestParams = useCallback((paramsFromTabulator = {}) => buildSupportTicketQueryParams(paramsFromTabulator, { includePagination: true, consumeResetPage: true }), [buildSupportTicketQueryParams]);

    const buildRequestParamsRef = useRef(buildRequestParams);
    buildRequestParamsRef.current = buildRequestParams;
    const getBuildRequestParams = useCallback((paramsFromTabulator) => buildRequestParamsRef.current(paramsFromTabulator), []);

    const buildSupportTicketQueryParamsRef = useRef(buildSupportTicketQueryParams);
    buildSupportTicketQueryParamsRef.current = buildSupportTicketQueryParams;

    const topFiltersMountedRef = useRef(false);
    useEffect(() => {
        if (!topFiltersMountedRef.current) {
            topFiltersMountedRef.current = true;
            return;
        }
        if (isRestoringRef.current || !tableBuiltRef.current) return;
        userInteractedRef.current = true;
        captureHScroll();
        resetToFirstPageRef.current = true;
        const table = getTable();
        if (table?.rowManager?.element) {
            table.rowManager.element.scrollTop = 0;
            const header = table.columnManager?.contentsElement;
            if (header) header.scrollLeft = 0;
        }
        refreshFromStart();
    }, [statusValue, contractorValue, typeValue, refreshFromStart]);

    // Keď sa tab stane aktívny a máme pending updates, spusti redraw
    useEffect(() => {
        if (!needsRedrawRef.current || !pendingRowIdsRef.current.size) return;
        if (isTableVisible()) scheduleRedraw();
    }, [state.selectedTab, isTableVisible, scheduleRedraw]);

    // Detail → list: patch only the already-loaded matching row in place.
    // Phase 5A: do not delete/reorder on filter/sort mismatch; do not insert if not loaded.
    useEffect(() => {
        const ticket = state.support_ticket_updated_in_detail;
        if (!ticket?.id) return;
        const table = getTable();
        if (!table?.getRow) {
            pendingTicketUpdateRef.current = ticket;
            dispatch({ type: 'support_ticket_updated_in_detail', payload: null });
            return;
        }
        pendingTicketUpdateRef.current = null;
        const result = updateSupportTicketTabulatorRowInPlace(table, ticket);
        if (result === 'updated') {
            pendingRowIdsRef.current.add(ticket.id);
            if (!isTableVisible()) needsRedrawRef.current = true;
            else scheduleRedraw();
        }
        dispatch({ type: 'support_ticket_updated_in_detail', payload: null });
    }, [state.support_ticket_updated_in_detail, dispatch, getTable, isTableVisible, scheduleRedraw]);

    // Označenie prečítané v detaile → aktualizuj riadok v tabuľke.
    // Dáta zoznamu sú z progressive loading (ajaxURL), nie zo state. lastTicketViewedUpdate je len signál;
    // nájdeme riadok v už načítanej tabuľke a aktualizujeme ho v mieste (row.update), alebo replaceData().
    useEffect(() => {
        const u = state.lastTicketViewedUpdate;
        if (!u) return;
        const table = getTable();
        if (!table?.getRow) return;
        const row = table.getRow(u.ticketId);
        if (row) {
            const data = row.getData?.();
            if (data) row.update({ ...data, is_unread: u.value }).catch(() => {});
        } else {
            refreshFromStart();
        }
    }, [state.lastTicketViewedUpdate, getTable]);

    useEffect(() => {
        return () => {
            if (rafRedrawRef.current != null) {
                cancelAnimationFrame(rafRedrawRef.current);
                rafRedrawRef.current = null;
            }
        };
    }, []);

    // ResizeObserver: keď sa záložka znova zobrazí (návrat z detailu), prekresli tabuľku – oprava neúplného náhľadu (rovnako ako intr003)
    const lastContainerHeightRef = useRef(0);
    useEffect(() => {
        const el = tableContainerRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(() => {
            const h = el.clientHeight;
            if (h > 0) {
                const wasHidden = lastContainerHeightRef.current <= 0;
                lastContainerHeightRef.current = h;
                if (wasHidden || needsRedrawRef.current) {
                    if (needsRedrawRef.current && pendingRowIdsRef.current.size) {
                        scheduleRedraw();
                    } else {
                        requestAnimationFrame(() => {
                            getTable()?.redraw?.(true);
                        });
                    }
                }
            } else {
                lastContainerHeightRef.current = 0;
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [scheduleRedraw, getTable]);

    // Pusher – support_ticket.updated/created (ticket_id), support_ticket.deleted (id); count pre Navbar
    useEffect(() => {
        const channel = getPusherChannel();
        if (!channel) return;

        const getId = (data) => data?.ticket_id ?? data?.id ?? null;
        const getDeletedId = (data) => data?.id ?? data?.ticket_id ?? null;
        const DEDUPE_MS = 800;
        const lastHandled = { key: '', at: 0 };
        const shouldSkip = (eventType, id) => {
            if (id == null) return true;
            const key = `${eventType}-${id}`;
            const now = Date.now();
            if (lastHandled.key === key && now - lastHandled.at < DEDUPE_MS) return true;
            lastHandled.key = key;
            lastHandled.at = now;
            return false;
        };

        const refreshNavbarCount = () => {
            getRequest('/api/support_tickets/count').then((res) => {
                dispatch({ type: 'load_support_tickets_open_count', count: res?.data?.count ?? res?.count ?? 0 });
            }).catch(() => {});
        };

        const captureScroll = () => {
            const t = getTable();
            if (!t?.rowManager?.element) return null;
            const holder = t.rowManager.element;
            const header = t.columnManager?.contentsElement;
            return { v: holder.scrollTop, h: header ? header.scrollLeft : holder.scrollLeft || 0 };
        };
        const restoreScroll = (pos) => {
            if (!pos) return;
            requestAnimationFrame(() => {
                const t = getTable();
                if (!t?.rowManager?.element) return;
                const holder = t.rowManager.element;
                const header = t.columnManager?.contentsElement;
                holder.scrollTop = pos.v;
                if (header) header.scrollLeft = pos.h;
            });
        };

        const onUpdated = (data) => {
            const id = getId(data);
            if (id == null || shouldSkip('updated', id)) return;
            const table = getTable();
            if (!table?.getRow) return;
            const scroll = captureScroll();
            getRequest(`/api/support_tickets/${id}/list_row`)
                .then((res) => {
                    const row = res?.data ?? res;
                    if (!row) return;
                    // Update: patch only if already loaded (no insert; no filter-driven delete).
                    const result = updateSupportTicketTabulatorRowInPlace(getTable(), row);
                    if (result === 'updated') {
                        pendingRowIdsRef.current.add(row.id);
                        if (!isTableVisible()) needsRedrawRef.current = true;
                        else scheduleRedraw();
                    }
                    restoreScroll(scroll);
                    refreshNavbarCount();
                })
                .catch(() => {});
        };

        const onCreated = (data) => {
            const id = getId(data);
            if (id == null || shouldSkip('created', id)) return;
            const table = getTable();
            if (!table?.getRow) return;
            const scroll = captureScroll();
            getRequest(`/api/support_tickets/${id}/list_row`)
                .then((res) => {
                    const row = res?.data ?? res;
                    if (!row) return;
                    const t = getTable();
                    const rc = t?.getRow?.(row.id);
                    if (rc) {
                        rc.update(row).catch(() => {});
                    } else if (ticketMatchesFilter(row, currentFilterRef.current)) {
                        t?.addData?.([row]);
                    } else {
                        restoreScroll(scroll);
                        refreshNavbarCount();
                        return;
                    }
                    pendingRowIdsRef.current.add(row.id);
                    if (!isTableVisible()) needsRedrawRef.current = true;
                    else scheduleRedraw();
                    restoreScroll(scroll);
                    refreshNavbarCount();
                })
                .catch(() => {});
        };

        const onDeleted = (data) => {
            const id = getDeletedId(data);
            if (id == null || shouldSkip('deleted', id)) return;
            const table = getTable();
            const row = table?.getRows?.().find((r) => r.getData?.()?.id === id);
            if (row) row.delete();
            refreshNavbarCount();
        };

        channel.bind('support_ticket.updated', onUpdated);
        channel.bind('support_ticket.created', onCreated);
        channel.bind('support_ticket.deleted', onDeleted);
        return () => {
            channel.unbind('support_ticket.updated', onUpdated);
            channel.unbind('support_ticket.created', onCreated);
            channel.unbind('support_ticket.deleted', onDeleted);
        };
    }, [getTable, isTableVisible, scheduleRedraw, dispatch, ticketMatchesFilter]);

    function checkStatus(value) {
        setStatusValue(value);
    }

    function checkContractor(value) {
        setContractorValue(value);
    }

    function checkType(value) {
        setTypeValue(value);
    }

    function EditRowFormatter(props) {
        const rowData = props.cell.getRow().getData();
        return (
            <div className='flex items-center justify-center w-full h-full'>
                <button type='button' onClick={() => dispatch({ type: 'create_new_tab', name: t('SupportTicketList.DetailTabTitleWithId', { id: rowData?.defined_id || rowData?.id || '' }), translationKey: 'SupportTicketList.DetailTabTitleWithId', translationParams: { id: rowData?.defined_id || rowData?.id || '' }, legacyLabel: t('SupportTicketList.DetailTabTitle'), page: 'SupportTicketDetail', props: { ticket: rowData, ticketId: rowData.id }, mask: 'help002' })}>
                    <EditRowIcon />
                </button>
            </div>
        );
    }

    const markAsViewedValue = async (ticketId, value) => {
        const formData = new FormData();
        formData.append('ticket_id', ticketId);
        formData.append("user_id", user.id);
        const url = !value ? `/api/support_tickets/${ticketId}/mark-as-viewed` : `/api/support_tickets/${ticketId}/mark-as-not-viewed`;
        try {
            await postRequest(url, formData, true);
            dispatch({ type: "updateTicketViewed", payload: ticketId, value: value });
        } catch (err) {}
        refreshFromStart();
    };

    
    function MailIconTemplate(props) {
    const tt = tRef.current;
    const rowData = props.cell.getRow().getData();
    const isUnread = rowData?.is_unread;
    return (
    <div className="flex items-center gap-2">
        {isUnread ? (
        <button type="button" title={tt('SupportTicketList.MarkAsRead')} onClick={() => markAsViewedValue(rowData.id, false)} className="hover:opacity-75">
            <UnseenIcon />
        </button>
        ) : (
        <button type="button" title={tt('SupportTicketList.MarkAsUnread')} onClick={() => markAsViewedValue(rowData.id, true)} className="hover:opacity-75">
            <SeenIcon />
        </button>
        )}
    </div>
    );
    }

    function TypeFormatter(props){
        const rowData = props.cell.getRow().getData();
        switch(rowData.type){
            case 'Info': {return <InfoIcon width={6} height={6}/>;}
            case 'Error': {return <ErrorIcon  width={6} height={6}/>;}
            case 'Task': {return <TaskIcon  width={6} height={6}/>;}
            case 'Permission': {return <PermissionIcon  width={6} height={6}/>;}
            case 'Need': {return <NeedIcon  width={6} height={6}/>;}
            case 'Emergency': {return <EmergencyIcon  width={6} height={6}/>;}
            default: {return <InfoIcon  width={6} height={6}/>;}
        }
    }

    function PriorityFormatter(props) {
        const tt = tRef.current;
        const rowData = props.cell.getRow().getData();
        switch(rowData.priority){
            case 'low': {return tt('SupportTicketList.Priority.low');}
            case 'normal': {return tt('SupportTicketList.Priority.normal');}
            case 'high': {return tt('SupportTicketList.Priority.high');}
            case 'blocker': {return tt('SupportTicketList.Priority.blocker');}
            default: {return tt('SupportTicketList.Priority.normal');}
        }
    }

    function StatusFormatter(props) {
        const tt = tRef.current;
        const rowData = props.cell.getRow().getData();
        const statusLabel = formatSupportTicketStatusLabel(rowData.status, tt);
        switch(rowData.status) {
            case 'Vytvorený':
                return <Tag className='bg-gray-400 '>
                            <span className="text-base font-normal">{statusLabel}</span>
                        </Tag>;
            case 'V riešení':
                return <Tag className='bg-yellow-400'>
                            <span className="text-base font-normal">{statusLabel}</span>
                        </Tag>;
            case 'Čaká sa':
                return <Tag className='bg-blue-300'>
                            <span className="text-base font-normal">{statusLabel}</span>
                        </Tag>;
            case 'Čaká sa na odpoveď':
                return <Tag className='bg-indigo-400'>
                            <span className="text-base font-normal">{statusLabel}</span>
                        </Tag>;
            case 'Pozastavený':
                return <Tag className='bg-red-300'>
                            <span className="text-base font-normal">{statusLabel}</span>
                        </Tag>;
            case 'Vyriešený':
                return <Tag className='bg-green-400'>
                            <span className="text-base font-normal">{statusLabel}</span>
                        </Tag>;
            default:
                return <Tag className='bg-gray-400 '>
                            <span className="text-base font-normal">{statusLabel}</span>
                        </Tag>;
        }
    }

    function ContractorFormatter(props) {
        const rowData = props.cell.getRow().getData();
        const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
        const dateTimeFmt = `${dateFmt} HH:mm`;
        return <div className='flex items-center justify-start w-full h-full'>
            {rowData?.contractor && <div><Avatar title={rowData?.contractor?.employee?.name || rowData?.contractor?.username} label={getUserInitials(rowData?.contractor?.employee?.name || rowData?.contractor?.username)} shape="circle" style={{ backgroundColor: getUserColor(rowData?.contractor?.username), color: '#ffffff',marginRight: '5px'}} /></div>}
            <div>{formatDate ? formatDate(rowData.created_at, dateTimeFmt, true) : DateTime.fromISO(rowData.created_at).toFormat(dateTimeFmt)}</div>
        </div>;
    }

    function DeleteFormatter(props) {
        const rowData = props.cell.getRow().getData();
        return (
            <div className="flex items-center justify-center w-full h-full">
                <button type="button" className="w-fit flex flex-row items-center justify-center p-1.5 border border-transparent rounded-md bg-red-900 text-white hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-600" onClick={() => deleteTicket(rowData.id)} title={t('SupportTicketList.DeleteTicket')}>
                    <DeleteIcon />
                </button>
            </div>
        );
    }

    function DateFormatter(props) {
        const rowData = props.cell.getRow().getData();
        const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
        const dateTimeFmt = `${dateFmt} HH:mm`;
        return <div className='flex items-center justify-start w-full h-full'>
            {rowData?.last_touched_by_user && <div><Avatar title={rowData?.last_touched_by_user?.employee?.name || rowData?.last_touched_by_user?.username} label={getUserInitials(rowData?.last_touched_by_user?.employee?.name || rowData?.last_touched_by_user?.username)} shape="circle" style={{ backgroundColor: getUserColor(rowData?.last_touched_by_user?.username), color: '#ffffff',marginRight: '5px'}} /></div>}
            <div>{formatDate ? formatDate(rowData.updated_at, dateTimeFmt, true) : DateTime.fromISO(rowData.updated_at).toFormat(dateTimeFmt)}</div>
        </div>;
    }

    function AwaitingUserFormatter(props) {
        const rowData = props.cell.getRow().getData();
        const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
        const deadline = rowData?.deadline
            ? DateTime.fromISO(rowData.deadline)
            : null;
        const today = DateTime.now().setZone(timezone).startOf("day");
        const isExpired = deadline && deadline < today;
        const isClosedStatus = rowData.status=="Vyriešený";

        return (
            <div className="flex items-center justify-start w-full h-full">
                {(!isClosedStatus && rowData?.awaiting_user) &&
                    <div key={rowData.awaiting_user_id}>
                        <Avatar
                            title={rowData.awaiting_user?.employee?.name || rowData.awaiting_user?.username}
                            label={getUserInitials(rowData.awaiting_user?.employee?.name || rowData.awaiting_user?.username)}
                            shape="circle"
                            style={{
                                backgroundColor: getUserColor(rowData.awaiting_user?.username),
                                color: "#ffffff",
                                marginRight: "5px"
                            }}
                        />
                    </div>
                }
                {deadline && (
                    <div className={isExpired && !isClosedStatus ? "text-red-500 font-semibold ml-1" : "ml-1"}>
                        {formatDate ? formatDate(deadline.toISO(), dateFmt, true) : deadline.toFormat(dateFmt)}
                    </div>
                )}
            </div>
        );
    }

    function awaitingUserSorter(a, b, rowA, rowB) {
        const dataA = rowA.getData();
        const dataB = rowB.getData();

        const deadlineA = dataA?.deadline
            ? DateTime.fromISO(dataA.deadline)
            : null;
        const deadlineB = dataB?.deadline
            ? DateTime.fromISO(dataB.deadline)
            : null;

        if (deadlineA && deadlineB) {
            if (deadlineA < deadlineB) return -1;
            if (deadlineA > deadlineB) return 1;
            return 0;
        }

        if (deadlineA && !deadlineB) return -1; // A má dátum, B nie
        if (!deadlineA && deadlineB) return 1;  // B má dátum, A nie

        return 0; // obaja nemajú deadline
    }

    const parseHeaderMulti = (headerValue) => {
        if (headerValue == null || headerValue === '') return [];
        if (Array.isArray(headerValue)) return headerValue.map((x) => String(x ?? '').trim()).filter(Boolean);
        if (typeof headerValue === 'object' && Array.isArray(headerValue?.value)) {
            return headerValue.value.map((x) => String(x ?? '').trim()).filter(Boolean);
        }
        return String(headerValue).split(/[\s,;|]+/).map((x) => x.trim()).filter(Boolean);
    };

    const normalizeText = (str) =>
        (str || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

    function normalizeFilter(headerValue, rowValue) {
        if (!headerValue) return true;
        return normalizeText(rowValue).includes(normalizeText(headerValue));
    }

    const dateFormatGlob = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
    const dateTimeFormatGlob = `${dateFormatGlob} HH:mm`;
    const serviceMinWidth = (title, filterKind = 'text') => {
        const base = filterKind === 'multiselect' ? 88 : 70;
        const perChar = 8;
        return Math.max(base + String(title || '').length * perChar, filterKind === 'multiselect' ? 120 : 100);
    };

    function dateOrUserFilter(headerValue, rowValue, rowData, filterParams) {
        if (!headerValue) return true;
        const raw = String(headerValue || '').trim();
        if (raw.startsWith('range:')) {
            const parts = raw.slice('range:'.length).split(':');
            if (parts.length >= 2 && parts[0] && parts[1]) {
                const from = DateTime.fromFormat(parts[0], 'yyyy-MM-dd').startOf('day');
                const to = DateTime.fromFormat(parts[1], 'yyyy-MM-dd').endOf('day');
                if (!from.isValid || !to.isValid) return true;
                const field = filterParams?.name;
                const source = field === 'waiting_for_action_by' ? rowData?.deadline : rowData?.[field];
                const dt = DateTime.fromISO(source || '');
                return dt.isValid ? dt >= from && dt <= to : false;
            }
        }
        const normalize = (str) =>
            (str || '')
                .toString()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase();
        const search = normalize(headerValue);
        const dateVariants = (dt) => {
            if (!dt || !dt.isValid) return '';
            return [
                dt.toFormat(dateFormatGlob),
                dt.toFormat(dateTimeFormatGlob),
                dt.toFormat('HH:mm'),
                dt.toFormat('yyyy-MM-dd'),
            ].join(' ');
        };
        if (filterParams?.name === 'updated_at' && rowData?.last_touched_by_user) {
            const touchedName = rowData.last_touched_by_user?.employee?.name || rowData.last_touched_by_user?.username;
            if (normalize(touchedName).includes(search)) return true;
        }
        if (filterParams?.name === 'updated_at') {
            const dt = DateTime.fromISO(rowData?.updated_at);
            if (normalize(dateVariants(dt)).includes(search)) return true;
        }
        if (filterParams?.name === 'waiting_for_action_by' && rowData?.deadline) {
            const dt = DateTime.fromISO(rowData.deadline);
            if (normalize(dateVariants(dt)).includes(search)) return true;
        }
        if (filterParams?.name === 'waiting_for_action_by' && rowData?.awaiting_user) {
            const uname = rowData.awaiting_user?.employee?.name || rowData.awaiting_user?.username;
            if (normalize(uname).includes(search)) return true;
        }
        if (filterParams?.name === 'created_at' && rowData?.contractor) {
            const touchedName = rowData.contractor?.employee?.name || rowData.contractor?.username;
            if (normalize(touchedName).includes(search)) return true;
        }
        if (filterParams?.name === 'created_at') {
            const dt = DateTime.fromISO(rowData?.created_at);
            if (normalize(dateVariants(dt)).includes(search)) return true;
        }
        return false;
    }

    const typeFilterOptions = useMemo(() => [
        { label: t('SupportTicketList.Type.Info'), value: 'Info' },
        { label: t('SupportTicketList.Type.Error'), value: 'Error' },
        { label: t('SupportTicketList.Type.Task'), value: 'Task' },
        { label: t('SupportTicketList.Type.Permission'), value: 'Permission' },
        { label: t('SupportTicketList.Type.Need'), value: 'Need' },
        { label: t('SupportTicketList.Type.Emergency'), value: 'Emergency' },
    ].sort((a, b) => a.label.localeCompare(b.label, locale || 'sk')), [t, locale]);

    const priorityFilterOptions = useMemo(() => [
        { label: t('SupportTicketList.Priority.low'), value: 'low' },
        { label: t('SupportTicketList.Priority.normal'), value: 'normal' },
        { label: t('SupportTicketList.Priority.high'), value: 'high' },
        { label: t('SupportTicketList.Priority.blocker'), value: 'blocker' },
    ], [t]);

    const statusFilterOptions = useMemo(() => buildSupportTicketStatusFilterOptions(t), [t]);

    useEffect(() => {
        const table = getTable();
        if (!table || !tableBuiltRef.current) return;
        applyTabulatorRuntimeI18n(table, tRef.current, {
            columnTitleKeys: SUPPORT_TICKET_COLUMN_I18N_KEYS,
            headerFilters: {
                type: { placeholder: t('Common.All'), options: typeFilterOptions },
                is_unread: {
                    placeholder: t('Common.All'),
                    options: [
                        { label: t('Common.Unread'), value: 'true' },
                        { label: t('Common.Read'), value: 'false' },
                    ],
                },
                priority: { placeholder: t('Common.All'), options: priorityFilterOptions },
                status: { placeholder: t('Common.All'), options: statusFilterOptions },
            },
            placeholder: t('Common.NoData'),
            dataLoaderLoading: t('Common.LoadingData'),
            reformatRows: true,
        });
    }, [locale, t, getTable, typeFilterOptions, priorityFilterOptions, statusFilterOptions]);

    const baseColumns = useMemo(() => [
        { title: '', field: 'editor', hozAlign: 'center', width: 44, headerSort: false, resizable: false, frozen: true, formatter: reactFormatter(<EditRowFormatter />) },
        { title: t('SupportTicketTable.Col.Id'), field: 'id', hozAlign: 'left', minWidth: serviceMinWidth(t('SupportTicketTable.Col.Id')), widthGrow: 0.8, headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: normalizeFilter },
        { title: t('SupportTicketTable.Col.Type'), field: 'type', hozAlign: 'center', headerHozAlign: 'center', formatter: reactFormatter(<TypeFormatter />), minWidth: 100, sorter: 'string', headerFilter: primeMultiSelectHeaderFilter,
            headerFilterParams: { placeholder: t('Common.All'), valueType: 'string', options: typeFilterOptions },
            headerFilterFunc: (headerValue, rowValue) => {
                if (!headerValue) return true;
                const selected = new Set(parseHeaderMulti(headerValue));
                return selected.has(String(rowValue));
            },
        },
        { title: '<span style="font-size:20px;line-height:1;">✉</span>', field: 'is_unread', hozAlign: 'center', headerHozAlign: 'center', minWidth: 92, resizable: false, formatter: reactFormatter(<MailIconTemplate />),
            headerFilter: primeMultiSelectHeaderFilter,
            headerFilterParams: { placeholder: t('Common.All'), valueType: 'string', options: [{ label: t('Common.Unread'), value: 'true' }, { label: t('Common.Read'), value: 'false' }] },
            headerFilterFunc: (headerValue, rowValue) => {
                if (!headerValue) return true;
                const selected = new Set(parseHeaderMulti(headerValue).map((x) => String(x).toLowerCase()));
                const rowKey = (rowValue === true || rowValue === 'true' || rowValue === 1 || rowValue === '1') ? 'true' : 'false';
                return selected.has(rowKey);
            },
        },
        { title: t('SupportTicketTable.Col.DefinedId'), field: 'defined_id', hozAlign: 'left', minWidth: serviceMinWidth(t('SupportTicketTable.Col.DefinedId')), widthGrow: 1, headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: normalizeFilter },
        { title: t('SupportTicketTable.Col.Priority'), field: 'priority', minWidth: serviceMinWidth(t('SupportTicketTable.Col.Priority'), 'multiselect'), formatter: reactFormatter(<PriorityFormatter />), resizable: true, headerFilter: primeMultiSelectHeaderFilter,
            headerFilterParams: { placeholder: t('Common.All'), valueType: 'string', options: priorityFilterOptions },
            headerFilterFunc: (headerValue, rowValue) => {
                if (!headerValue) return true;
                const selected = new Set(parseHeaderMulti(headerValue));
                return selected.has(String(rowValue));
            },
        },
        { title: t('SupportTicketTable.Col.Theme'), field: 'theme', hozAlign: 'left', minWidth: serviceMinWidth(t('SupportTicketTable.Col.Theme')), widthGrow: 2, headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: normalizeFilter },
        { title: t('SupportTicketTable.Col.Status'), field: 'status', minWidth: serviceMinWidth(t('SupportTicketTable.Col.Status'), 'multiselect'), formatter: reactFormatter(<StatusFormatter />), resizable: true, headerFilter: primeMultiSelectHeaderFilter,
            headerFilterParams: { placeholder: t('Common.All'), valueType: 'string', options: statusFilterOptions },
            headerFilterFunc: (headerValue, rowValue) => headerFilterMatchesSupportTicketStatus(headerValue, rowValue),
        },
        { title: t('SupportTicketTable.Col.MaskNumber'), field: 'mask_number', minWidth: serviceMinWidth(t('SupportTicketTable.Col.MaskNumber')), resizable: true, headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: normalizeFilter },
        { title: t('SupportTicketTable.Col.WaitingDeadline'), field: 'waiting_for_action_by', resizable: true, minWidth: DATE_USER_HEADER_FILTER_COL_MIN_WIDTH, widthGrow: 1.3, formatter: reactFormatter(<AwaitingUserFormatter />), sorter: awaitingUserSorter, headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterParams: { dateFormat: dateFormatGlob, dateTimeFormat: dateTimeFormatGlob }, headerFilterFunc: dateOrUserFilter, headerFilterFuncParams: { name: 'waiting_for_action_by' } },
        { title: t('SupportTicketTable.Col.Created'), field: 'created_at', minWidth: DATE_USER_HEADER_FILTER_COL_MIN_WIDTH, widthGrow: 1.2, formatter: reactFormatter(<ContractorFormatter />), resizable: true, headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterParams: { dateFormat: dateFormatGlob, dateTimeFormat: dateTimeFormatGlob }, headerFilterFunc: dateOrUserFilter, headerFilterFuncParams: { name: 'created_at' } },
        { title: t('SupportTicketTable.Col.Updated'), field: 'updated_at', minWidth: DATE_USER_HEADER_FILTER_COL_MIN_WIDTH, widthGrow: 1.2, formatter: reactFormatter(<DateFormatter />), resizable: true, headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterParams: { dateFormat: dateFormatGlob, dateTimeFormat: dateTimeFormatGlob }, headerFilterFunc: dateOrUserFilter, headerFilterFuncParams: { name: 'updated_at' } },
    ], [t, locale, typeFilterOptions, priorityFilterOptions, statusFilterOptions, dateFormatGlob, dateTimeFormatGlob]);

    const columns = useMemo(() => {
        const tail = user?.role === 'admin' && user?.permissions?.includes('delete service_task')
            ? [{ title: t('SupportTicketTable.Col.Delete'), field: 'custom_delete', minWidth: 110, hozAlign: 'center', resizable: false, headerSort: false, formatter: reactFormatter(<DeleteFormatter />) }]
            : [];
        return [...baseColumns, ...tail, { field: 'contractor_id', visible: false }];
    }, [baseColumns, user?.role, user?.permissions, t]);

    function renderTicketType(type) {
        switch(type) {
        case 'Info':
            return <div className='flex gap-2 text-gray-800 items-center'><InfoIcon width={8} height={8}/><span>{t('SupportTicketList.Type.Info')}</span></div>;
        case 'Error':
            return <div className='flex gap-2 text-gray-800 items-center'><ErrorIcon width={8} height={8}/><span>{t('SupportTicketList.Type.Error')}</span></div>;
        case 'Task':
            return <div className='flex gap-2 text-gray-800 items-center'><TaskIcon width={8} height={8}/><span>{t('SupportTicketList.Type.Task')}</span></div>;
        case 'Permission':
            return <div className='flex gap-2 text-gray-800 items-center'><PermissionIcon width={8} height={8}/><span>{t('SupportTicketList.Type.Permission')}</span></div>;
        case 'Need':
            return <div className='flex gap-2 text-gray-800 items-center'><NeedIcon width={8} height={8}/><span>{t('SupportTicketList.Type.Need')}</span></div>;
        case 'Emergency':
            return <div className='flex gap-2 text-gray-800 items-center'><EmergencyIcon width={8} height={8}/><span>{t('SupportTicketList.Type.Emergency')}</span></div>;
        default:
            return <div className='flex gap-2 text-gray-800 items-center'><InfoIcon width={8} height={8}/><span>{t('SupportTicketList.Type.Info')}</span></div>;
        }
    }

    const reject = () => {
        toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Rejected'), detail: t('ServiceTask.RequestCancelled'), life: 3000 });
    };

    const acceptTicket = (id) => {
        deleteRequest(`/api/support_tickets/${id}`)
            .then(() => {
                getRequest('/api/support_tickets/count').then((res) => {
                    dispatch({ type: 'load_support_tickets_open_count', count: res?.data?.count ?? res?.count ?? 0 });
                }).catch(() => {});
                toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), life: 3000, detail: t('SupportTicketList.DeleteSuccess') });
                refreshFromStart();
            })
            .catch((reason) => {
                toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Rejected'), detail: t('SupportTicketList.DeleteFailed'), life: 3000 });
            });
    };

    function deleteTicket(id) {
        confirmDialog({
            message: t('SupportTicketList.DeleteConfirmMessage'),
            header: t('SupportTicketList.DeleteConfirmHeader'),
            icon: 'pi pi-info-circle',
            acceptClassName: 'p-button-danger',
            acceptLabel: t('Common.No'),
            rejectLabel: t('Common.Yes'),
            accept: reject,
            reject: () => acceptTicket(id)
        });
    }

    const itemRenderer = (item, options) => (
        <a className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px]" onClick={options.onClick}>
            <span className={`${item.icon} text-primary`} />
            <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
        </a>
    );

    function setSupportSelects() {
        const headerFilters = getTable()?.getHeaderFilters?.() || [];
        const headerFilterObj = headerFilters.reduce((acc, f) => {
            if (!f?.field) return acc;
            acc[f.field] = f.value;
            return acc;
        }, {});
        const filters = { ...headerFilterObj, statusValue, contractorValue, typeValue };
        setUserMaskFilter("SupportTicketList", "set_filters", null, filters, null);
        setExpandedKeys({});
    }

    function clearSupportSelects() {
        setStatusValue(SUPPORT_TICKET_SCOPE_OPEN);
        setContractorValue("Všetky");
        setTypeValue("All");
        getTable()?.clearHeaderFilter?.();
        setUserMaskFilter("SupportTicketList", "set_filters", null, {}, null);
        setExpandedKeys({});
    }

    const setMask = (item, value) => {
        const columnVisible = { ...columnsVisible };
        columnVisible[item] = value;
        setColumnsVisible(columnVisible);
        if (value) getTable()?.showColumn?.(item); else getTable()?.hideColumn?.(item);
        setTimeout(() => getTable()?.redraw?.(true), 0);
    };

    const changeMarkings = (value) => {
        const columnVisible = { ...columnsVisible };
        for (const val in columnVisible) {
            if (val !== 'editor' && val !== 'custom_delete') {
                columnVisible[val] = value;
                if (value) getTable()?.showColumn?.(val); else getTable()?.hideColumn?.(val);
            }
        }
        setColumnsVisible(columnVisible);
    };

    const getMaskFields = useMemo(() => (
        columns
            .map((item) => item?.field)
            .filter((field) => field && field !== 'editor' && field !== 'custom_delete' && field !== 'contractor_id')
    ), [columns]);
    const maskFieldToColumn = useMemo(
        () => columns.reduce((acc, item) => {
            if (item?.field) acc[item.field] = item;
            return acc;
        }, {}),
        [columns],
    );
    const getMaskColumnLabel = useCallback((field) => {
        if (field === 'is_unread') return t('Common.Read');
        const title = maskFieldToColumn[field]?.title;
        if (typeof title === 'string') {
            return title.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() || field;
        }
        return field;
    }, [maskFieldToColumn, t]);

    const hasApplicableStandardMask = useMemo(() => {
        const row = (state.standard_mask_filters || []).find((m) => m.page === 'SupportTicketList');
        if (!row) return false;
        const attributes = sanitizeTabulatorLayoutAttributes(safeParse(row.attributes, null));
        return Array.isArray(attributes) && attributes.length > 1;
    }, [state.standard_mask_filters]);

    const confirmApplyStandardLayoutToUserMask = () => {
        const standard = (state.standard_mask_filters || []).find((m) => m.page === 'SupportTicketList');
        if (!standard) {
            toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.StandardMask'), detail: t('ServiceTask.NoStandardMaskSaved'), life: 4000 });
            return;
        }
        let attributes = [];
        let cols = {};
        try {
            attributes = sanitizeTabulatorLayoutAttributes(safeParse(standard.attributes, []));
            cols = safeParse(standard.columns, {});
        } catch (_) {
            toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.StandardMask'), detail: t('ServiceTask.StandardMaskInvalidData'), life: 4000 });
            return;
        }
        if (!Array.isArray(attributes) || attributes.length <= 1) {
            toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.StandardMask'), detail: t('ServiceTask.StandardMaskInvalidLayout'), life: 4000 });
            return;
        }
        const colsForSave = { ...(cols || {}) };
        confirmDialog({
            message: t('ServiceTask.ApplyStandardMaskPrompt'),
            header: t('ServiceTask.StandardMask'),
            icon: 'pi pi-info-circle',
            acceptLabel: t('Common.Use'),
            rejectLabel: t('Common.Cancel'),
            accept: () => {
                setUserMaskFilter('SupportTicketList', 'all', colsForSave, null, attributes, null, () => {
                    const table = getTable();
                    if (!table) return;
                    isRestoringRef.current = true;
                    try { table.setColumnLayout?.(attributes); } catch (_) {}
                    setColumnsVisible(colsForSave);
                    Object.keys(colsForSave).forEach((field) => {
                        if (colsForSave[field] === false) table.hideColumn?.(field);
                        else table.showColumn?.(field);
                    });
                    setTimeout(() => {
                        isRestoringRef.current = false;
                        table.redraw?.(true);
                        captureHScroll();
                        refreshFromStart();
                    }, 0);
                });
            },
        });
    };

    const canSetStandardMask = user?.permissions?.includes('set standard mask');

    const confirmSaveStandardMask = () => {
        confirmDialog({
            message: t('ServiceTask.SaveStandardMaskPrompt'),
            header: t('ServiceTask.SaveStandardMaskTitle'),
            icon: 'pi pi-info-circle',
            acceptLabel: t('Common.Save'),
            rejectLabel: t('Common.Cancel'),
            accept: () => {
                const table = getTable();
                const layout = getTabulatorLayoutForStandardMask(table);
                if (!layout) {
                    toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Mask'), detail: t('ServiceTask.TableNotReady'), life: 3500 });
                    return;
                }
                if (!Object.values(columnsVisible).some(Boolean)) {
                    toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.StandardMask'), detail: t('ServiceTask.AtLeastOneColumnVisible'), life: 3500 });
                    return;
                }
                setStandardMaskFilter('SupportTicketList', columnsVisible, layout.attributes);
            },
        });
    };

    const buildSupportTicketExportQueryString = useCallback((opts = {}) => {
        const exportParams = buildSupportTicketQueryParamsRef.current({}, { includePagination: false, consumeResetPage: false });
        return buildSupportTicketExportQueryStringFromParams(exportParams, opts);
    }, []);

    const runSupportTicketPrint = async () => {
        try {
            setPrintChoiceVisible(false);
            setDialogText(t('Common.PreparingPrint'));
            setVisible(true);
            const suffix = buildSupportTicketExportQueryString({
                includePrintColumns: true,
                printLayout: printLayoutChoice,
                printOrientation: printOrientationChoice,
                printColumns: getSupportTicketPrintColumnOrder(getTable, columnsVisible),
            });
            const html = await fetchTextGet(`/api/support_tickets/export/print${suffix}`);
            const opened = openPrintHtmlWindow(html);
            if (!opened) {
                toast?.current?.show({ severity: 'warn', summary: t('Common.Print'), life: 4000, detail: t('ServiceTask.EnablePopups') });
                return;
            }
        } catch (e) {
            if (String(e?.message || '').includes('(404)')) {
                getTable()?.print(false, true);
                toast?.current?.show({ severity: 'warn', summary: t('Common.Print'), life: 3500, detail: t('ServiceTask.PrintFallback') });
            } else {
                toast?.current?.show({ severity: 'error', summary: t('Common.Print'), life: 5000, detail: e?.message || t('ServiceTask.PrintFailed') });
            }
        } finally {
            setVisible(false);
        }
    };

    const exportSupportTicketXlsx = async () => {
        const exportDocTitle = t(EXPORT_DOCUMENT_TITLE_KEYS.SUPPORT_TICKET_LIST);
        try {
            setDialogText(t('Common.PreparingExport'));
            setVisible(true);
            const suffix = buildSupportTicketExportQueryString();
            const defaultXlsx = xlsxDownloadFilename(exportDocTitle);
            const { blob, filename } = await downloadBlobGet(`/api/support_tickets/export/xlsx${suffix}`, defaultXlsx);
            triggerBlobDownload(blob, filename || defaultXlsx);
            toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Done'), life: 3000, detail: t('ServiceTask.ExportDownloaded') });
        } catch (e) {
            if (String(e?.message || '').includes('(404)')) {
                const table = getTable();
                table?.download('xlsx', xlsxDownloadFilename(exportDocTitle), { sheetName: excelSheetTitleFromDocumentTitle(exportDocTitle) });
                toast?.current?.show({ severity: 'warn', summary: t('Common.Export'), life: 3500, detail: t('ServiceTask.ExportFallback') });
            } else {
                toast?.current?.show({ severity: 'error', summary: t('Common.Export'), life: 5000, detail: e?.message || t('ServiceTask.ExportFailed') });
            }
        } finally {
            setVisible(false);
        }
    };

    const scrollToFirst = () => scrollToFirstUtil(getTable());
    const scrollUp = () => scrollUpUtil(getTable());
    const scrollDown = () => scrollDownUtil(getTable());
    const scrollToLast = () => scrollToLastUtil(getTable());

    const items = [
        { key: '0', label: t('ServiceTask.Actions'), icon: 'pi pi-sliders-v', template: itemRenderer, items: [
            { key: '0_1', label: t('ServiceTask.SetFilter'), icon: 'pi pi-filter', command: setSupportSelects, template: itemRenderer },
            { key: '0_2', label: t('ServiceTask.ClearFilter'), icon: 'pi pi-filter-slash', command: clearSupportSelects, template: itemRenderer },
            { key: '0_3', label: t('ServiceTask.SetSort'), icon: 'pi pi-sort-alt', command: () => {
                const rawSorters = getTable()?.getSorters?.() || [];
                const pureSorters = rawSorters.map(s => {
                    const field = s.field || s.column?.getField?.();
                    if (!field) return null;
                    const dir = s.dir === 'desc' ? 'desc' : 'asc';
                    return { column: field, dir };
                }).filter(Boolean);
                setUserMaskFilter("SupportTicketList", "set_sorters", null, null, null, pureSorters);
                setExpandedKeys({});
            }, template: itemRenderer },
            { key: '0_4', label: t('ServiceTask.ClearSort'), icon: 'pi pi-sort-alt-slash', command: () => {
                setUserMaskFilter("SupportTicketList", "set_sorters", null, null, null, []);
                getTable()?.setSort?.([]);
                setExpandedKeys({});
                userInteractedRef.current = true;
                captureHScroll();
                refreshFromStart();
            }, template: itemRenderer },
            { key: '0_5', label: t('ServiceTask.SetMask'), icon: 'pi pi-server', command: () => { setMaskVisible(true); setExpandedKeys({}); }, template: itemRenderer },
            ...(hasApplicableStandardMask ? [{
                key: '0_5s',
                label: t('ServiceTask.StandardMask'),
                icon: 'pi pi-clone',
                command: () => { confirmApplyStandardLayoutToUserMask(); setExpandedKeys({}); },
                template: itemRenderer,
            }] : []),
        ]}
    ];

    const handleClickOutside = (event) => {
        if (overlayRef.current && !overlayRef.current.contains(event.target)) {
            setExpandedKeys({});
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    useEffect(() => {
        if (maskAppliedRef.current) return;
        const mask = state.user_mask_filters?.filter(m => m.page === "SupportTicketList") || [];
        if (mask.length === 0) return;
        const table = getTable();
        if (!table) return;
        const raw = safeParse(mask[0].filters, {});
        if (!raw || typeof raw !== "object" || Array.isArray(raw)) return;
        setStatusValue(normalizeSupportTicketListScope(raw?.statusValue));
        setContractorValue(raw?.contractorValue || "Všetky");
        setTypeValue(raw?.typeValue || "All");
        maskAppliedRef.current = true;
    }, [state.user_mask_filters, getTable]);

    return (
        <>
            <div className='relative flex flex-col bg-gray-100 w-full h-[calc(100vh_-_137px)] py-4 px-4 overflow-hidden min-h-0'>
                <div className="w-full px-4 py-4 bg-[#333] text-white border rounded-sm">
                    <h2 className="text-left text-xl font-semibold whitespace-nowrap">{t('SupportTicketList.PageTitle')}</h2>
                </div>
                <div className="flex flex-row flex-wrap items-start bg-white shadow-md p-4 gap-4">
                    <div className="flex flex-row flex-wrap items-center gap-2">
                        <label className="font-semibold">{t('SupportTicketList.Filter.Status')}:</label>
                        <select className="bg-[#333] text-white px-4 h-11 rounded-md" value={statusValue} onChange={(e) => checkStatus(e.target.value)}>
                            <option value="Otvorené">{t('SupportTicketList.Filter.OpenTickets')}</option>
                            <option value="Zatvorené">{t('SupportTicketList.Filter.ClosedTickets')}</option>
                        </select>
                        <select className="bg-[#333] text-white px-4 h-11 rounded-md" value={contractorValue} onChange={(e) => checkContractor(e.target.value)}>
                            <option value="Všetky">{t('SupportTicketList.Filter.AllTickets')}</option>
                            <option value="Mnou vytvorené">{t('SupportTicketList.Filter.OpenedByMe')}</option>
                            <option value="Spomenutý">{t('SupportTicketList.Filter.Mentioned')}</option>
                            <option value="Vykonávateľ">{t('SupportTicketList.Filter.Executor')}</option>
                        </select>
                        <label className="font-semibold">{t('SupportTicketList.Filter.TicketType')}:</label>
                        <select className="bg-[#333] text-white px-4 h-11 rounded-md" value={typeValue} onChange={(e) => checkType(e.target.value)}>
                            <option value="All">{t('Common.All')}</option>
                            <option value="Info">{t('SupportTicketList.Type.Info')}</option>
                            <option value="Error">{t('SupportTicketList.Type.Error')}</option>
                            <option value="Task">{t('SupportTicketList.Type.Task')}</option>
                            <option value="Permission">{t('SupportTicketList.Type.Permission')}</option>
                            <option value="Need">{t('SupportTicketList.Type.Need')}</option>
                            <option value="Emergency">{t('SupportTicketList.Type.Emergency')}</option>
                        </select>
                    </div>
                    <div className="bg-[#333] text-white rounded-md h-11 px-2 inline-flex items-center gap-1">
                        <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={() => setPrintChoiceVisible(true)} title={t('Common.Print')}><PrintIcon className="shrink-0" /></button>
                        <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={exportSupportTicketXlsx} title={t('Common.ExportToExcel')}><ExportIcon className="shrink-0" /></button>
                        <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={() => { userInteractedRef.current = true; captureHScroll(); refreshFromStart(); }} title={t('Common.Refresh')}><RefreshIcon className="shrink-0" /></button>
                        <div className="h-9 inline-flex items-center text-white/70"><VerticalLineIcon /></div>
                        <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={scrollToFirst} title={t('Icon.First')}><BackToFirstIcon /></button>
                        <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={scrollUp} title={t('Icon.Previous')}><BackIcon /></button>
                        <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={scrollDown} title={t('Icon.Next')}><ForwardIcon /></button>
                        <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={scrollToLast} title={t('Icon.Last')}><ForwardToLastIcon /></button>
                        <div className="h-9 inline-flex items-center text-white/70"><VerticalLineIcon /></div>
                        <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={() => openFavoriteDialog(t('SupportTicketList.PageTitle'), 'SupportTicketList')} title={t('Common.AddToFavorites')}><FavoriteIcon /></button>
                        <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={() => setFastView('SupportTicketList', columnsVisible, columns)} title={t('Common.QuickView')}><PriceIcon /></button>
                        {canSetStandardMask ? (
                            <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={confirmSaveStandardMask} title={t('Common.SaveStandardMask')}>
                                <i className="pi pi-bookmark text-white" aria-hidden="true" />
                            </button>
                        ) : null}
                    </div>
                    <div className="relative w-full md:w-auto">
                        <div className="absolute z-50 top-0 left-0 bg-[#333] rounded-md shadow-lg overflow-hidden" ref={overlayRef}>
                            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full bg-[#333] text-white" />
                        </div>
                    </div>
                </div>
                <div ref={tableContainerRef} className="flex-1 min-h-0 overflow-hidden">
                    <ReactTabulator
                            key="support-ticket-list"
                            className='h-[calc(100vh_-_313px)]'
                            onRef={(r) => (ref.current = r?.current ?? r)}
                            columns={columns}
                            events={{
                                headerFilterChanged: () => {
                                    if (isRestoringRef.current) return;
                                    userInteractedRef.current = true;
                                    captureHScroll();
                                    refreshFromStart();
                                },
                                sortChanged: () => {
                                    if (!isRestoringRef.current) userInteractedRef.current = true;
                                },
                                dataSorted: () => {
                                    if (!isRestoringRef.current) userInteractedRef.current = true;
                                },
                                tableBuilt: () => {
                                    const pending = pendingTicketUpdateRef.current;
                                    if (pending?.id) {
                                        const table = getTable();
                                        if (table?.getRow) {
                                            pendingTicketUpdateRef.current = null;
                                            const result = updateSupportTicketTabulatorRowInPlace(table, pending);
                                            if (result === 'updated') {
                                                pendingRowIdsRef.current.add(pending.id);
                                                if (!isTableVisible()) needsRedrawRef.current = true;
                                                else scheduleRedraw();
                                            }
                                        }
                                    }
                                    isRestoringRef.current = true;
                                    userInteractedRef.current = false;
                                    const table = getTable();
                                    const effective = getEffectiveMask('SupportTicketList', state);
                                    if (effective?.type === 'user' && effective.userMask) {
                                        const m = effective.userMask;
                                        try {
                                            table?.blockRedraw?.();
                                            const attributes = sanitizeTabulatorLayoutAttributes(safeParse(m.attributes, []));
                                            if (Array.isArray(attributes) && attributes.length > 1) table?.setColumnLayout?.(attributes);
                                            const rawFilters = safeParse(m.filters, {});
                                            const rf = rawFilters && typeof rawFilters === 'object' && !Array.isArray(rawFilters) ? rawFilters : {};
                                            setStatusValue(normalizeSupportTicketListScope(rf.statusValue));
                                            setContractorValue(rf.contractorValue || "Všetky");
                                            setTypeValue(rf.typeValue || "All");
                                            Object.entries(rf).filter(([k]) => !['statusValue', 'contractorValue', 'typeValue'].includes(k)).forEach(([field, value]) => {
                                                if (value != null && value !== '') table?.setHeaderFilterValue?.(field, value, true);
                                            });
                                            const typeVal = rf.type ?? (rf.typeValue === 'All' || !rf.typeValue ? '' : rf.typeValue);
                                            table?.setHeaderFilterValue?.('type', typeVal !== '' && typeVal != null ? typeVal : '', true);
                                            const parsedSorters = safeParse(m.sorters, []);
                                            const sorters = Array.isArray(parsedSorters)
                                                ? parsedSorters.map((s) => {
                                                    if (typeof s?.column === 'string') {
                                                        const dir = s.dir === 'desc' ? 'desc' : 'asc';
                                                        return { column: s.column, dir };
                                                    }
                                                    const field = s.field || s.column?.getField?.();
                                                    if (!field) return null;
                                                    const dir = s.dir === 'desc' ? 'desc' : 'asc';
                                                    return { column: field, dir };
                                                }).filter(Boolean)
                                                : [];
                                            if (sorters.length > 0) table?.setSort?.(sorters);
                                            const json_columns = safeParse(m.columns, {});
                                            if (json_columns && typeof json_columns === 'object' && !Array.isArray(json_columns)) {
                                                setColumnsVisible(json_columns);
                                                Object.entries(json_columns).forEach(([col, visible]) => {
                                                    if (visible === false) table?.hideColumn?.(col); else table?.showColumn?.(col);
                                                });
                                            }
                                            maskAppliedRef.current = true;
                                        } finally {
                                            table?.restoreRedraw?.(true);
                                        }
                                    } else if (effective?.type === 'standard' && effective.standardMask) {
                                        const m = effective.standardMask;
                                        try {
                                            table?.blockRedraw?.();
                                            const attributes = sanitizeTabulatorLayoutAttributes(safeParse(m.attributes, []));
                                            if (Array.isArray(attributes) && attributes.length > 1) table?.setColumnLayout?.(attributes);
                                            const json_columns = safeParse(m.columns, {});
                                            if (json_columns && typeof json_columns === 'object' && !Array.isArray(json_columns)) {
                                                setColumnsVisible(json_columns);
                                                Object.entries(json_columns).forEach(([col, visible]) => {
                                                    if (visible === false) table?.hideColumn?.(col); else table?.showColumn?.(col);
                                                });
                                            }
                                            checkStatus(statusValue);
                                            checkContractor(contractorValue);
                                            checkType(typeValue);
                                        } finally {
                                            table?.restoreRedraw?.(true);
                                        }
                                    } else {
                                        checkStatus(statusValue);
                                        checkContractor(contractorValue);
                                        checkType(typeValue);
                                        applyDefaultListLayout();
                                    }
                                    tableBuiltRef.current = true;
                                    setTimeout(() => { isRestoringRef.current = false; }, 0);
                                },
                                renderComplete: () => restoreHScroll(),
                                columnMoved: () => {
                                    if (!userInteractedRef.current || isRestoringRef.current) return;
                                    setUserMaskFilter("SupportTicketList", "column_move", null, null, getTable()?.getColumnLayout?.());
                                },
                                columnResized: () => {
                                    if (!userInteractedRef.current || isRestoringRef.current) return;
                                    setUserMaskFilter("SupportTicketList", "column_resized", null, null, getTable()?.getColumnLayout?.());
                                },
                                dataChanged: () => {
                                    if (isRestoringRef.current) return;
                                    const f = currentFilterRef.current;
                                    if (f.statusValue != null) checkStatus(f.statusValue);
                                    if (f.contractorValue != null) checkContractor(f.contractorValue);
                                    if (f.typeValue != null) checkType(f.typeValue);
                                },
                            }}
                            options={{
                                index: "id",
                                layout: "fitColumns",
                                renderHorizontal: "virtual",
                                rowHeight: 42,
                                headerSortTristate: true,
                                dataLoader: true,
                                dataLoaderLoading: t('Common.LoadingData'),
                                dataLoaderError: t('Common.LoadFailed'),
                                placeholder: t('Common.NoData'),
                                height: "calc(100vh - 313px)",
                                progressiveLoad: "scroll",
                                progressiveLoadScrollMargin: 80,
                                progressiveLoadDelay: 200,
                                paginationSize: pageSizePc,
                                pagination: false,
                                filterMode: "remote",
                                sortMode: "remote",
                                ajaxURL: "/api/support_tickets/paged",
                                ajaxParams: () => ({}),
                                ajaxRequestFunc: createDedupedAjaxRequest(getBuildRequestParams, tabulatorGet, inFlightRef, completedCacheRef, isRestoringRef, lastResponseRef),
                                ajaxRequesting: () => {
                                    if (tableBuiltRef.current && !isRestoringRef.current) captureHScroll();
                                    const table = getTable();
                                    if (table?.initialized) table.alert?.(t('SupportTicketList.TableLoadingData'));
                                    return true;
                                },
                                ajaxResponse: (url, params, response) => {
                                    getTable()?.clearAlert?.();
                                    if (response && typeof response === 'object') lastResponseRef.current = response;
                                    return response;
                                },
                                ajaxError: () => {
                                    getTable()?.clearAlert?.();
                                },
                                movableColumns: true,
                                printAsHtml: true,
                                printHeader: () => buildCanonicalPrintHeaderHtml({
                                    branding: state?.branding,
                                    productFallbackSrc: defaultProductLogoSrc,
                                    companySettingsPrint: state?.company_settings_print,
                                    moduleSettings: state?.module_settings,
                                    title: t('SupportTicketList.PrintHeader'),
                                    printDateLabel: t('Print.DateLabel'),
                                    // Keep long theme/description cells wrapping on Tabulator fallback print (404 → table.print).
                                    bodyHtml: `<style>.tabulator-print-table td,.tabulator-print-table th{white-space:pre-wrap!important;overflow-wrap:anywhere!important;word-break:break-word!important;overflow:visible!important;text-overflow:clip!important;max-height:none!important;}</style>`,
                                }),
                                printFooter: () => buildCanonicalPrintFooterHtml({ companySettingsPrint: state?.company_settings_print }),
                                printRowRange: "active",
                                autoResize: true,
                                scrollToColumnIfVisible: false,
                            }}
                        />
                    </div>
            </div>
            <PrintOptionsDialog
                visible={printChoiceVisible}
                onHide={() => setPrintChoiceVisible(false)}
                orientation={printOrientationChoice}
                onOrientationChange={setPrintOrientationChoice}
                layout={printLayoutChoice}
                onLayoutChange={setPrintLayoutChoice}
                onConfirm={runSupportTicketPrint}
            />
            <TabulatorMaskDialog
                visible={maskVisible}
                onHide={() => setMaskVisible(false)}
                title={t('Common.SetMask')}
                fields={getMaskFields}
                checkedMap={columnsVisible}
                onToggleField={setMask}
                onSelectAll={() => changeMarkings(true)}
                onDeselectAll={() => changeMarkings(false)}
                onSave={() => {
                    setUserMaskFilter('SupportTicketList', 'set_columns', columnsVisible, null, null);
                    setMaskVisible(false);
                }}
                selectAllLabel={t('Common.SelectAll')}
                deselectAllLabel={t('Common.DeselectAll')}
                saveLabel={t('Common.Save')}
                getFieldLabel={getMaskColumnLabel}
                inputIdPrefix="support-ticket-mask"
            />
        </>);
} 
